import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  ArrowRight, 
  User, 
  Award, 
  Heart, 
  Brain, 
  Clock, 
  Phone, 
  Mail, 
  Camera, 
  Upload, 
  Plus, 
  X, 
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  Save
} from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';
import { toast } from 'react-toastify';

import { DashboardLayout } from '../layouts/DashboardLayout';
import useCreateClinician from '../hooks/Clinic/useCreateClinician';
import { useFetchSpeciality } from '../hooks/Resources/speciality/useFetchSpeciality';
import useFetchModalities from '../hooks/Resources/modality/useFetchModalityResource';
import { useFetchAgeRange } from '../hooks/Resources/age/useFetchAgeRange';
import  useLanguagesResource  from '../hooks/Resources/language/useLanguagesResource';
import { useFetchInsuranceResource } from '../hooks/Resources/insurance/useFetchInsuranceResource';
import useFetchSessionType from '../hooks/Resources/session/useFetchSessionTypeResource';

// MultiSelect Component
interface MultiSelectOption {
  value: string;
  label: string;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  selectedValues: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

const MultiSelect: React.FC<MultiSelectProps> = ({ options, selectedValues, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = (value: string) => {
    if (selectedValues.includes(value)) {
      onChange(selectedValues.filter(v => v !== value));
    } else {
      onChange([...selectedValues, value]);
    }
  };

  const selectedLabels = options
    .filter(option => selectedValues.includes(option.value))
    .map(option => option.label);

  return (
    <div className="relative">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-3 border border-gray-300 rounded-lg mt-1 cursor-pointer bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center justify-between">
          <div className="flex flex-wrap gap-1 min-h-6">
            {selectedLabels.length > 0 ? (
              selectedLabels.map((label, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800"
                >
                  {label}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const option = options.find(opt => opt.label === label);
                      if (option) {
                        handleToggle(option.value);
                      }
                    }}
                    className="ml-1 hover:text-blue-600"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))
            ) : (
              <span className="text-gray-500">{placeholder || 'Select options'}</span>
            )}
          </div>
          <div className={`transform transition-transform ${isOpen ? 'rotate-180' : ''}`}>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {options.map((option) => (
            <div
              key={option.value}
              onClick={() => handleToggle(option.value)}
              className="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer"
            >
              <div className="flex items-center">
                <div className={`w-4 h-4 border rounded mr-3 flex items-center justify-center ${
                  selectedValues.includes(option.value) 
                    ? 'bg-blue-600 border-blue-600' 
                    : 'border-gray-300'
                }`}>
                  {selectedValues.includes(option.value) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </div>
                <span className="text-sm">{option.label}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Clinician Data Interface
interface ClinicianData {
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  npiNumber: string;
  title: string;
  role: string;
  status: string;
  specialties: string[];
  licenses: string[];
  profile: {
    bio: string;
    profileImage: string;
    rates: Array<{ sessionType: string; amount: number; currency: string }>;
    sessionTypes: Array<{ type: string; duration: number; rate?: number }>;
    insuranceAccepted: string[];
    languages: string[];
    modalities: string[];
    ageRanges: string[];
  };
}

const INITIAL_CLINICIAN: ClinicianData = {
  firstName: '',
  lastName: '',
  fullName: '',
  email: '',
  phone: '',
  npiNumber: '',
  title: '',
  role: '',
  status: 'active',
  specialties: [],
  licenses: [],
  profile: {
    bio: '',
    profileImage: '',
    rates: [],
    sessionTypes: [],
    insuranceAccepted: [],
    languages: ['English'],
    modalities: [],
    ageRanges: []
  }
};

const AddClinicianPage = () => {
  const navigate = useNavigate();
  const { createClinician } = useCreateClinician();
  
  // Form state
  const [clinician, setClinician] = useState<ClinicianData>(INITIAL_CLINICIAN);
  const [currentStep, setCurrentStep] = useState(1);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Resource data
  const { specialties } = useFetchSpeciality();
  const { modalities } = useFetchModalities();
  const { ages, fetchAgeRanges } = useFetchAgeRange();
  const { languages, fetchLanguages } = useLanguagesResource ();
  const { insurances } = useFetchInsuranceResource();
  const { sessionTypes, fetchSessionTypeData } = useFetchSessionType();

  // Form helpers
  const [newRate, setNewRate] = useState({ sessionType: '', amount: '', currency: 'USD' });
  const [newSessionType, setNewSessionType] = useState({ type: '', duration: '', rate: '' });

  // Fetch resources on mount
  useEffect(() => {
    fetchAgeRanges();
    fetchLanguages();
    fetchSessionTypeData();
  }, [fetchAgeRanges, fetchLanguages, fetchSessionTypeData]);

  // Update full name when first/last name changes
  useEffect(() => {
    setClinician(prev => ({
      ...prev,
      fullName: `${prev.firstName} ${prev.lastName}`.trim()
    }));
  }, [clinician.firstName, clinician.lastName]);

  const handleInputChange = (field: string, value: any) => {
    if (field.startsWith('profile.')) {
      const profileField = field.replace('profile.', '');
      setClinician(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          [profileField]: value
        }
      }));
    } else {
      setClinician(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const addToArray = (field: string, newItem: any) => {
    const currentArray = field.startsWith('profile.') 
      ? clinician.profile[field.replace('profile.', '') as keyof typeof clinician.profile] as any[]
      : clinician[field as keyof ClinicianData] as any[];
    
    handleInputChange(field, [...(currentArray || []), newItem]);
  };

  const removeFromArray = (field: string, index: number) => {
    const currentArray = field.startsWith('profile.') 
      ? clinician.profile[field.replace('profile.', '') as keyof typeof clinician.profile] as any[]
      : clinician[field as keyof ClinicianData] as any[];
    
    handleInputChange(field, currentArray.filter((_, i) => i !== index));
  };

  // Step validation
  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1: // Basic Info
        return !!(clinician.firstName && clinician.lastName && clinician.email && clinician.title);
      case 2: // Professional
        return !!(clinician.specialties.length > 0 && clinician.profile.modalities.length > 0);
      case 3: // Services
        return !!(clinician.profile.sessionTypes.length > 0 || clinician.profile.rates.length > 0);
      case 4: // Contact
        return !!(clinician.phone);
      default:
        return true;
    }
  };

  const canProceed = validateStep(currentStep);
  const canGoBack = currentStep > 1;

  const nextStep = () => {
    if (canProceed && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (canGoBack) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(4)) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await createClinician(clinician as any);
      toast.success('Clinician created successfully!');
      navigate('/practice/clinicians');
    } catch (error) {
      toast.error('Failed to create clinician');
      console.error('Error creating clinician:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { id: 1, title: 'Basic Info', icon: User, description: 'Personal information' },
    { id: 2, title: 'Professional', icon: Award, description: 'Credentials & specialties' },
    { id: 3, title: 'Services', icon: Heart, description: 'Session types & rates' },
    { id: 4, title: 'Contact', icon: Phone, description: 'Contact information' }
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Basic Information */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <User className="w-5 h-5" />
                Basic Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    placeholder="Enter first name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Last Name *</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    placeholder="Enter last name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="Enter email address"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Credentials *</label>
                  <input 
                    type="text" 
                    placeholder="e.g., PhD, LMFT, MFCC" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Professional Title</label>
                  <input 
                    type="text" 
                    placeholder="e.g., Licensed Marriage and Family Therapist" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">NPI Number</label>
                  <input 
                    type="text" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.npiNumber}
                    onChange={(e) => handleInputChange('npiNumber', e.target.value)}
                    placeholder="Enter NPI number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                  <select 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={clinician.status}
                    onChange={(e) => handleInputChange('status', e.target.value)}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Biography */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Biography</h3>
              <div className="relative">
                <textarea 
                  rows={6} 
                  maxLength={750} 
                  placeholder="Write a compelling bio that introduces yourself to potential clients..." 
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={clinician.profile.bio}
                  onChange={(e) => handleInputChange('profile.bio', e.target.value)}
                />
                <div className="flex justify-between items-center mt-2">
                  <p className="text-xs text-gray-500">Aim for 3 well-written paragraphs introducing your experience and approach</p>
                  <div className="text-xs text-gray-500">{clinician.profile.bio.length}/750 characters</div>
                </div>
              </div>
            </div>

            {/* Profile Photo */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Profile Photo</h3>
              <div className="flex items-center gap-6">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center overflow-hidden">
                  {clinician.profile.profileImage ? (
                    <img 
                      src={clinician.profile.profileImage} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Camera className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                          handleInputChange('profile.profileImage', e.target?.result as string);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    className="hidden"
                    id="profile-upload"
                  />
                  <label
                    htmlFor="profile-upload"
                    className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <Upload className="w-4 h-4" />
                    Upload Photo
                  </label>
                  <p className="text-sm text-gray-500 mt-2">Recommended: 400x400px, JPG or PNG</p>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Specialties */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <Award className="w-5 h-5" />
                Specialties *
              </h2>
              <div className="mb-4">
                <div className="flex flex-wrap gap-2">
                  {clinician.specialties.map((specialty, index) => (
                    <span key={index} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                      {specialty}
                      <button 
                        className="hover:bg-blue-200 rounded-full p-0.5"
                        onClick={() => removeFromArray('specialties', index)}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Add Specialty</label>
                <MultiSelect
                  options={specialties.map(s => ({ value: s.id, label: s.name }))}
                  selectedValues={clinician.specialties}
                  onChange={(selected) => handleInputChange('specialties', selected)}
                  placeholder="Select specialties..."
                />
              </div>
            </div>

            {/* Professional Information */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-6">Professional Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Modalities *</label>
                  <MultiSelect
                    options={modalities.map(m => ({ value: m.id, label: m.name }))}
                    selectedValues={clinician.profile.modalities}
                    onChange={(selected) => handleInputChange('profile.modalities', selected)}
                    placeholder="Select modalities"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Age Ranges</label>
                  <MultiSelect
                    options={ages.map(a => ({ value: a.id, label: a.range_name }))}
                    selectedValues={clinician.profile.ageRanges}
                    onChange={(selected) => handleInputChange('profile.ageRanges', selected)}
                    placeholder="Select age ranges"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Languages</label>
                  <MultiSelect
                    options={languages.map(l => ({ value: l.id, label: l.name }))}
                    selectedValues={clinician.profile.languages}
                    onChange={(selected) => handleInputChange('profile.languages', selected)}
                    placeholder="Select languages"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Insurance Accepted</label>
                  <MultiSelect
                    options={insurances.map(i => ({ value: i.id, label: i.company_name }))}
                    selectedValues={clinician.profile.insuranceAccepted}
                    onChange={(selected) => handleInputChange('profile.insuranceAccepted', selected)}
                    placeholder="Select insurance accepted"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Session Types */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Session Types & Rates
              </h2>
              <div className="space-y-4">
                {clinician.profile.sessionTypes?.map((session, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      <select
                        value={session.type}
                        onChange={(e) => {
                          const updated = [...clinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], type: e.target.value };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Session Type</option>
                        {sessionTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name} 
                          </option>
                        ))}
                      </select>
                      <input
                        type="number"
                        value={session.duration}
                        onChange={(e) => {
                          const updated = [...clinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], duration: parseInt(e.target.value) };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Duration (minutes)"
                      />
                      <input
                        type="number"
                        value={session.rate || ''}
                        onChange={(e) => {
                          const updated = [...clinician.profile.sessionTypes!];
                          updated[index] = { ...updated[index], rate: parseFloat(e.target.value) };
                          handleInputChange('profile.sessionTypes', updated);
                        }}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Rate ($)"
                      />
                    </div>
                    <button
                      onClick={() => removeFromArray('profile.sessionTypes', index)}
                      className="text-red-500 text-sm hover:text-red-700"
                    >
                      Remove Session Type
                    </button>
                  </div>
                ))}
                <div className="mt-4 space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <select
                      value={newSessionType.type}
                      onChange={(e) => setNewSessionType({ ...newSessionType, type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select Session Type</option>
                      {sessionTypes.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name} 
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      placeholder="Duration (minutes)"
                      value={newSessionType.duration}
                      onChange={(e) => setNewSessionType({ ...newSessionType, duration: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <input
                      type="number"
                      placeholder="Rate ($)"
                      value={newSessionType.rate}
                      onChange={(e) => setNewSessionType({ ...newSessionType, rate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => {
                      if (newSessionType.type && newSessionType.duration) {
                        addToArray('profile.sessionTypes', { 
                          type: newSessionType.type, 
                          duration: parseInt(newSessionType.duration),
                          rate: newSessionType.rate ? parseFloat(newSessionType.rate) : undefined
                        });
                        setNewSessionType({ type: '', duration: '', rate: '' });
                      }
                    }}
                    className="text-blue-500 text-sm hover:text-blue-700 flex items-center"
                  >
                    <Plus className="mr-1" size={16} />
                    Add Session Type
                  </button>
                </div>
              </div>
            </div>

            {/* Rates */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-6">Additional Rates</h3>
              <div className="space-y-4">
                {clinician.profile.rates?.map((rate, index) => (
                  <div key={index} className="bg-gray-50 p-4 rounded-lg space-y-2">
                    <input
                      value={rate.sessionType}
                      onChange={(e) => {
                        const updated = [...clinician.profile.rates!];
                        updated[index] = { ...updated[index], sessionType: e.target.value };
                        handleInputChange('profile.rates', updated);
                      }}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Session type"
                    />
                    <div className="flex gap-2">
                      <input
                        type="number"
                        value={rate.amount}
                        onChange={(e) => {
                          const updated = [...clinician.profile.rates!];
                          updated[index] = { ...updated[index], amount: parseFloat(e.target.value) };
                          handleInputChange('profile.rates', updated);
                        }}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Amount"
                      />
                      <select
                        value={rate.currency}
                        onChange={(e) => {
                          const updated = [...clinician.profile.rates!];
                          updated[index] = { ...updated[index], currency: e.target.value };
                          handleInputChange('profile.rates', updated);
                        }}
                        className="w-20 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="USD">USD</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                    <button
                      onClick={() => removeFromArray('profile.rates', index)}
                      className="text-red-500 text-sm hover:text-red-700"
                    >
                      Remove Rate
                    </button>
                  </div>
                ))}
                <div className="mt-4 space-y-2">
                  <input
                    placeholder="Session Type"
                    value={newRate.sessionType}
                    onChange={(e) => setNewRate({ ...newRate, sessionType: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="Amount"
                      value={newRate.amount}
                      onChange={(e) => setNewRate({ ...newRate, amount: e.target.value })}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <select
                      value={newRate.currency}
                      onChange={(e) => setNewRate({ ...newRate, currency: e.target.value })}
                      className="w-20 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                    </select>
                  </div>
                  <button
                    onClick={() => {
                      if (newRate.sessionType && newRate.amount) {
                        addToArray('profile.rates', { 
                          sessionType: newRate.sessionType, 
                          amount: parseFloat(newRate.amount),
                          currency: newRate.currency 
                        });
                        setNewRate({ sessionType: '', amount: '', currency: 'USD' });
                      }
                    }}
                    className="text-blue-500 text-sm hover:text-blue-700 flex items-center"
                  >
                    <Plus className="mr-1" size={16} />
                    Add Rate
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Contact Information */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                <Phone className="w-5 h-5" />
                Contact Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                  <input 
                    type="tel" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" 
                    value={clinician.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="Enter phone number"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email (Review)</label>
                  <input 
                    type="email" 
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50" 
                    value={clinician.email}
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Summary */}
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-600">Name:</p>
                  <p className="font-medium">{clinician.fullName || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Credentials:</p>
                  <p className="font-medium">{clinician.title || 'Not provided'}</p>
                </div>
                <div>
                  <p className="text-gray-600">Specialties:</p>
                  <p className="font-medium">{clinician.specialties.length} selected</p>
                </div>
                <div>
                  <p className="text-gray-600">Session Types:</p>
                  <p className="font-medium">{clinician.profile.sessionTypes.length} configured</p>
                </div>
              </div>
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => navigate('/practice/clinicians')}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                </button>
                <div>
                  <Breadcrumbs items={[{ label: 'Add Clinician' }]} />
                  <h1 className="text-xl font-semibold text-gray-900">Add New Clinician</h1>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => setShowPreview(!showPreview)}
                  className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <Eye className="w-4 h-4" />
                  Preview
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="flex">
          {/* Sidebar Navigation */}
          <div className="w-64 bg-white border-r border-gray-200 h-screen sticky top-0">
            <div className="p-6">
              <nav className="space-y-2">
                {steps.map((step) => (
                  <button
                    key={step.id}
                    onClick={() => setCurrentStep(step.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      currentStep === step.id 
                        ? 'bg-blue-50 text-blue-700 border border-blue-200' 
                        : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <step.icon className="w-5 h-5" />
                    <div className="flex-1">
                      <div className="font-medium">{step.title}</div>
                      <div className="text-xs opacity-75">{step.description}</div>
                    </div>
                    {validateStep(step.id) && (
                      <Check className="w-4 h-4 text-green-600" />
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 p-8">
            <div className="mx-auto max-w-4xl">
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    Step {currentStep} of {steps.length}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Math.round((currentStep / steps.length) * 100)}% Complete
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(currentStep / steps.length) * 100}%` }}
                  ></div>
                </div>
              </div>

              {/* Preview Card */}
              {showPreview && (
                <div className="bg-white rounded-xl shadow-md p-8 mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                    <Eye className="mr-2 text-blue-600" size={24} />
                    Preview
                  </h2>
                  <div className="max-w-md mx-auto">
                    <div className="bg-white rounded-lg shadow-md p-6 border">
                      <div className="flex items-center gap-4 mb-4">
                        <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                          {clinician.profile.profileImage ? (
                            <img 
                              src={clinician.profile.profileImage} 
                              alt="Profile" 
                              className="w-full h-full object-cover rounded-full"
                            />
                          ) : (
                            <User className="w-8 h-8 text-blue-600" />
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">{clinician.fullName || 'Name not provided'}</h3>
                          <p className="text-sm text-gray-600">{clinician.title || 'Credentials not provided'}</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700 mb-4 line-clamp-3">
                        {clinician.profile.bio || 'No biography available'}
                      </p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {clinician.specialties.slice(0, 3).map((specialty, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                            {specialty}
                          </span>
                        ))}
                      </div>
                      <div className="flex justify-between items-center text-sm text-gray-600">
                        <span>Status: {clinician.status}</span>
                        <span>{clinician.profile.languages?.length || 0} languages</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Step Content */}
              <AnimatePresence mode="wait">
                {renderStep()}
              </AnimatePresence>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                <button
                  onClick={prevStep}
                  disabled={!canGoBack}
                  className="flex items-center gap-2 px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <div className="flex items-center gap-3">
                  {currentStep < 4 ? (
                    <button
                      onClick={nextStep}
                      disabled={!canProceed}
                      className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <button
                      onClick={handleSubmit}
                      disabled={isSubmitting || !validateStep(4)}
                      className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4" />
                          Create Clinician
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AddClinicianPage;